import { useEffect, useState } from "react";
import { Box, Card, Stack, Typography } from "@mui/material";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import { homeService } from "../../api/service.ts";
import type { AttendanceEntry, TrainingSummary } from "../../api/types";

const MEDALS = ["🥇", "🥈", "🥉"];

function Tile({ label, value }: { label: string; value: string }) {
    return (
        <Box sx={{ flex: 1, minWidth: 0, textAlign: "center", px: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }} noWrap>
                {label}
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: "1.35rem", lineHeight: 1.2 }} noWrap>{value}</Typography>
        </Box>
    );
}

/** Classifica presenze: a pari presenze stessa medaglia */
function Podium({ title, entries, total }: { title: string; entries: AttendanceEntry[]; total: number }) {
    let rank = 0;
    return (
        <Box sx={{ flex: "1 1 180px", minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                {title}
            </Typography>
            {entries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Ancora nessun dato</Typography>
            ) : entries.map((e, i) => {
                if (i > 0 && e.count < entries[i - 1].count) rank = i;
                return (
                    <Stack key={e.playerId} direction="row" alignItems="center" gap={1}>
                        <Box component="span" sx={{ width: 22 }}>{MEDALS[Math.min(rank, 2)]}</Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>{e.nickname}</Typography>
                        <Typography variant="body2" color="text.secondary">{e.count}{total ? ` / ${total}` : ""}</Typography>
                    </Stack>
                );
            })}
        </Box>
    );
}

/** Allenamenti della stagione selezionata e giocatori più presenti (allenamenti e partite separati) */
export default function TrainingStatsCard() {
    const [data, setData] = useState<TrainingSummary | null>(null);

    useEffect(() => {
        homeService.getTrainingSummary().then(setData).catch(() => setData(null));
    }, []);

    if (!data) return null;

    return (
        <Card sx={{ borderRadius: 4, p: 1.5 }}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1, px: 0.5 }}>
                <FitnessCenterIcon fontSize="small" color="action" />
                <Typography variant="subtitle2">Allenamenti e presenze</Typography>
            </Stack>
            {data.sessions === 0 && data.matches === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
                    Nessun allenamento o partita registrati in questa stagione
                </Typography>
            ) : (
                <>
                    <Stack direction="row" sx={{ mb: 1.5 }}>
                        <Tile label="Allenamenti" value={String(data.sessions)} />
                        <Tile label="Set giocati" value={String(data.sets)} />
                        <Tile label="Media presenti" value={data.avgPresent.toLocaleString("it-IT")} />
                    </Stack>
                    <Stack direction="row" gap={2} flexWrap="wrap" sx={{ px: 0.5 }}>
                        <Podium title="Più presenti agli allenamenti" entries={data.topTrainings} total={data.sessions} />
                        <Podium title="Più presenti in partita" entries={data.topMatches} total={data.matches} />
                    </Stack>
                </>
            )}
        </Card>
    );
}
